/**
 * ReportListView — two-column dataset/report browser. Extracted from
 * `Reports.tsx` to keep that file under the 500-line cap.
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertTriangle,
  CheckSquare,
  ChevronDown,
  ClipboardList,
  Copy,
  CreditCard,
  Database,
  Edit,
  Eye,
  FileCheck,
  FileText,
  FolderPlus,
  History,
  MoreVertical,
  Package,
  Plus,
  RefreshCw,
  Search,
  Globe,
  Settings,
  Share2,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

import { ReportListDialogs, type ReportListDialogsProps } from './ReportListDialogs';
import {
  type AnalyseDashboard,
  type Dataset,
} from '../../lib/api/reporting';

const DATASET_ICONS: Record<string, React.ReactNode> = {
  'Status Change Log': <History className="h-8 w-8 text-blue-500" />,
  'Questionnaire': <ClipboardList className="h-8 w-8 text-purple-500" />,
  'Purchase Orders': <ShoppingCart className="h-8 w-8 text-green-500" />,
  'NCR': <AlertTriangle className="h-8 w-8 text-red-500" />,
  'GRN': <Package className="h-8 w-8 text-amber-500" />,
  'Credit Score': <CreditCard className="h-8 w-8 text-cyan-500" />,
  'Contract': <FileCheck className="h-8 w-8 text-indigo-500" />,
  'Approvals': <CheckSquare className="h-8 w-8 text-emerald-500" />,
};

function getDatasetIcon(name: string): React.ReactNode {
  return DATASET_ICONS[name] || <Database className="h-8 w-8 text-muted-foreground" />;
}

/**
 * View-specific props (left column dataset picker + right column table).
 * Dialog state/handlers are inherited from `ReportListDialogsProps` to
 * keep the prop surface aligned with the dialog mount.
 */
interface ReportListViewProps extends ReportListDialogsProps {
  reports: AnalyseDashboard[];
  selectedDataset: Dataset | null;
  loading: boolean;
  isCustomer: boolean;
  setSelectedDataset: (d: Dataset | null) => void;
  openEditReport: (report: AnalyseDashboard) => void;
  openDuplicateReport: (report: AnalyseDashboard) => void;
  openShareReport: (report: AnalyseDashboard) => void;
  openPublishReport: (report: AnalyseDashboard) => void;
  openRevisionHistory: (report: AnalyseDashboard) => void;
  openDownloadLogs: () => void;
  openFiscalCalendar: () => void;
  onViewReport: (report: AnalyseDashboard) => void;
  onRefresh: () => void;
}

export function ReportListView(props: ReportListViewProps) {
  const {
    datasets,
    categories,
    reports,
    selectedDataset,
    setSelectedDataset,
    loading,
    isCustomer,
    setIsCreateReportOpen,
    setIsCreateCategoryOpen,
    setReportToDelete,
    setIsDeleteReportOpen,
    openEditReport,
    openDuplicateReport,
    openShareReport,
    openPublishReport,
    openRevisionHistory,
    openDownloadLogs,
    openFiscalCalendar,
    onViewReport,
    onRefresh,
    setNewReport,
  } = props;

  const [reportSearch, setReportSearch] = useState('');

  const datasetReports = useMemo(() => {
    let filtered = reports;
    if (selectedDataset) {
      filtered = filtered.filter((r) => r.dataset === selectedDataset.id);
    }
    if (reportSearch) {
      const s = reportSearch.toLowerCase();
      filtered = filtered.filter(
        (r) => r.name.toLowerCase().includes(s) || r.description?.toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [reports, selectedDataset, reportSearch]);

  const handleAddReportClick = () => {
    if (selectedDataset) {
      setNewReport((prev) => ({ ...prev, dataset: selectedDataset.id }));
    }
    setIsCreateReportOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
          <p className="text-muted-foreground">
            Browse the datasets you have available, then build queries and charts on top of them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isCustomer && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={openFiscalCalendar}>
                  Fiscal calendar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openDownloadLogs}>
                  Download audit
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isCustomer && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsCreateReportOpen(true)}>
                  <FileText className="h-4 w-4 mr-2" />
                  New Query
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsCreateCategoryOpen(true)}>
                  <FolderPlus className="h-4 w-4 mr-2" />
                  New Category
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ReportQuickStats
        totalReports={reports.length}
        categories={categories.length}
        datasets={datasets.length}
        activeReports={reports.filter((r) => r.is_enabled).length}
      />

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Datasets — the data domains available to query */}
        <div className="lg:col-span-5">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Database className="h-5 w-5" />
                    Datasets
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Real data domains in the platform. Pick one to filter queries below or to start a new query against it.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {datasets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No datasets available</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {datasets.map((dataset) => {
                    const isSelected = selectedDataset?.id === dataset.id;
                    const queryCount =
                      dataset.saved_query_count ??
                      reports.filter((r) => r.dataset === dataset.id).length;
                    const fieldCount = dataset.available_field_count ?? dataset.fields?.length ?? 0;
                    return (
                      <button
                        key={dataset.id}
                        onClick={() => setSelectedDataset(isSelected ? null : dataset)}
                        className={`relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all text-center hover:shadow-md hover:border-primary/50 ${
                          isSelected
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-border bg-card hover:bg-accent/50'
                        }`}
                      >
                        {getDatasetIcon(dataset.name)}
                        <span className="text-sm font-medium leading-tight">{dataset.name}</span>
                        <div className="flex flex-col items-center gap-0.5 text-[10px] text-muted-foreground">
                          <span>{fieldCount} field{fieldCount === 1 ? '' : 's'}</span>
                          <span>{queryCount} saved quer{queryCount === 1 ? 'y' : 'ies'}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Saved queries table */}
        <div className="lg:col-span-7">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-5 w-5" />
                  {selectedDataset
                    ? `${selectedDataset.name} — Saved Queries`
                    : 'All Saved Queries'}
                  <Badge variant="outline" className="ml-1">
                    {datasetReports.length}
                  </Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={reportSearch}
                      onChange={(e) => setReportSearch(e.target.value)}
                      className="pl-8 h-8 w-[160px] text-sm"
                    />
                  </div>
                  {isCustomer && (
                    <Button size="sm" onClick={handleAddReportClick}>
                      <Plus className="h-4 w-4 mr-1" />
                      New Query
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {datasetReports.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3 opacity-50" />
                  <h3 className="font-medium mb-1 text-sm">
                    {selectedDataset
                      ? `No saved queries on ${selectedDataset.name} yet`
                      : 'Pick a dataset to start'}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    {selectedDataset
                      ? 'Build your first query against this dataset.'
                      : 'Click a dataset on the left to see its saved queries or build a new one.'}
                  </p>
                  {isCustomer && selectedDataset && (
                    <Button size="sm" variant="outline" onClick={handleAddReportClick}>
                      <Plus className="h-4 w-4 mr-1" />
                      New Query
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Sharing</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[40px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {datasetReports.map((report) => (
                      <TableRow
                        key={report.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onViewReport(report)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div>
                              <div className="font-medium text-sm">{report.name}</div>
                              {report.description && (
                                <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                                  {report.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {report.category_name || 'Uncategorized'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {report.shared_with_display || report.shared_with}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(report.created_at), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={report.is_enabled ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {report.is_enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              asChild
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <RowMenuItem icon={<Eye className="h-4 w-4 mr-2" />} label="View" onSelect={() => onViewReport(report)} />
                              {isCustomer && (
                                <>
                                  <RowMenuItem icon={<Edit className="h-4 w-4 mr-2" />} label="Edit" onSelect={() => openEditReport(report)} />
                                  <RowMenuItem icon={<Copy className="h-4 w-4 mr-2" />} label="Duplicate" onSelect={() => openDuplicateReport(report)} />
                                  <RowMenuItem icon={<Share2 className="h-4 w-4 mr-2" />} label="Share" onSelect={() => openShareReport(report)} />
                                  <RowMenuItem icon={<Globe className="h-4 w-4 mr-2" />} label="Publish" onSelect={() => openPublishReport(report)} />
                                  <RowMenuItem icon={<History className="h-4 w-4 mr-2" />} label="Revisions" onSelect={() => openRevisionHistory(report)} />
                                  <DropdownMenuSeparator />
                                  <RowMenuItem
                                    icon={<Trash2 className="h-4 w-4 mr-2" />}
                                    label="Delete"
                                    destructive
                                    onSelect={() => {
                                      setReportToDelete(report);
                                      setIsDeleteReportOpen(true);
                                    }}
                                  />
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ReportListDialogs {...props} />
    </div>
  );
}

interface RowMenuItemProps {
  icon: React.ReactNode;
  label: string;
  destructive?: boolean;
  onSelect: () => void;
}

function RowMenuItem({ icon, label, destructive, onSelect }: RowMenuItemProps) {
  return (
    <DropdownMenuItem
      className={destructive ? 'text-destructive' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {icon}
      {label}
    </DropdownMenuItem>
  );
}

interface ReportQuickStatsProps {
  totalReports: number;
  categories: number;
  datasets: number;
  activeReports: number;
}

function ReportQuickStats({ totalReports, categories, datasets, activeReports }: ReportQuickStatsProps) {
  const items = [
    { label: 'Datasets', value: datasets },
    { label: 'Saved Queries', value: totalReports },
    { label: 'Active', value: activeReports },
    { label: 'Categories', value: categories },
  ];
  return (
    <div className="grid gap-4 md:grid-cols-4">
      {items.map((item) => (
        <Card key={item.label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {item.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{item.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

